import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useContacts } from '../hooks/useContacts';
import { useAuth } from '../hooks/useAuth';
import { useOptions } from '../contexts/OptionsContext';
import { useOfertas } from '../hooks/useOfertas';
import { useAreas } from '../hooks/useAreas';
import { useTiposEmpresa } from '../hooks/useTiposEmpresa';
import { useBriefingSummaries } from '../hooks/useBriefingSummaries';
import { useProximoPassoCounts } from '../hooks/useProximoPassoCounts';
import { useContactEmploymentStatus } from '../hooks/useContactEmploymentStatus';
import { useEmailsPendentes } from '../hooks/useEmailsPendentes';
import { ContactTable } from './ContactTable';
import FUPModal from './FUPModal';
import EmailModal from './EmailModal';
import ContactQuickAddModal from './ContactQuickAddModal';
import VCardImportModal from './VCardImportModal';
import CreateOfertaModal from './CreateOfertaModal';
import CreateAreaModal from './CreateAreaModal';
import OfertaDetailModal from './OfertaDetailModal';
import AreasListModal from './AreasListModal';
import AreaDetailModal from './AreaDetailModal';
import HistoryModal from './HistoryModal';
import AIContextGlobalModal from './AIContextGlobalModal';
import AIContextPanelEmpresa from './AIContextPanelEmpresa';
import EmailsPendentesModal from './EmailsPendentesModal';
import EmailsHubModal from './EmailsHubModal';
import OportunidadesEscondidasModal from './OportunidadesEscondidasModal';
import DominiosNovosModal from './DominiosNovosModal';
import ApolloEnrichModal from './ApolloEnrichModal';
import F8EvidenceReviewModal from './F8EvidenceReviewModal';
import EmailDetailDrawer from './EmailDetailDrawer';
import ApolloEnrichContactModal from './ApolloEnrichContactModal';
import { useEvidences } from '../hooks/useEvidences';
import { Building2, User, Filter as FilterIcon, Search as SearchIcon, MapPin, CheckCircle2, XCircle, Tag, Layers } from 'lucide-react';
import DashboardHeader from './layout/DashboardHeader';
import FilterPopover from './dashboard/FilterPopover';
import { colaboradores } from '../utils/stageHelpers';

const CreateEmpresaModal = ({ onClose, onSave }) => {
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!nomeEmpresa.trim()) return;
    setSaving(true);
    await onSave({ empresa: nomeEmpresa.trim().toUpperCase(), company_name: nomeEmpresa.trim() });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Building2 className="text-blue-600" size={20} />
          Nova Empresa
        </h2>
        <div className="mb-4">
          <label className="block text-sm text-slate-600 mb-1">Nome da Empresa</label>
          <input
            type="text"
            value={nomeEmpresa}
            onChange={(e) => setNomeEmpresa(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Ex: TECNISA"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!nomeEmpresa.trim() || saving}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            {saving ? 'Salvando...' : 'Criar Empresa'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { contatos, loading, updateContact, updateEmpresa, createEmpresa } = useContacts();
  const { signOut, user } = useAuth();
  const { dealStages, loadingOptions } = useOptions();
  const { ofertas, createOferta, updateOferta, buildHITLPatchManual, buildHITLPatchDataFup, refetch: refetchOfertas } = useOfertas();
  const { areas, loading: loadingAreas, createArea, updateArea } = useAreas();
  const { stats: emailStats } = useEmailsPendentes();
  const { papeis: papelOptions, setores: setorOptions } = useTiposEmpresa();
  const { byOferta: briefingByOferta } = useBriefingSummaries();
  const { byOferta: ppCountByOferta, refetch: refetchPPCounts } = useProximoPassoCounts();
  // Frente C: status de employments para badge "ex" na tabela de contatos
  const contatoIdsParaStatus = useMemo(
    () => (contatos || []).filter(c => c.id && !c._isVirtual).map(c => c.id),
    [contatos]
  );
  const { statusMap: employmentStatusMap } = useContactEmploymentStatus(contatoIdsParaStatus);

  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const [focusEmpresaId, setFocusEmpresaId] = useState(null);

  // Onda 4/7 abr/26: deep-link de /proprietarios (PJ) — focar empresa + preencher busca + expandir.
  // Bug fix abr/26: o filtro padrão "buyer-only" escondia empresas landowner/outros.
  // Estratégia: só limpa location.state quando papelOptions já carregou — assim o
  // setSelectedPapelOptions(papelOptions) realmente aplica (evita race async).
  useEffect(() => {
    const id = location.state?.focusEmpresaId;
    if (!id || loading) return;
    if (!papelOptions || papelOptions.length === 0) return; // aguarda lookup carregar
    setFocusEmpresaId(id);
    const empresa = contatos?.find((c) => c.empresas?.id === id)?.empresas;
    if (empresa) {
      setSearchTerm(empresa.company_name || empresa.empresa || '');
    }
    setSelectedPapelOptions(papelOptions); // todos os papéis = desbloqueia landowner/service_provider
    setMostrarInativas(true);
    window.history.replaceState({}, '');
  }, [location.state, contatos, loading, papelOptions]);
  const [selectedStageOptions, setSelectedStageOptions] = useState([]);
  const [selectedResponsavelOptions, setSelectedResponsavelOptions] = useState([]);
  const [selectedAreaOptions, setSelectedAreaOptions] = useState([]);
  // Fix filtros_dashboard 2026-04-26: removido ofertaSearchTerm (pouco usado);
  // substituido por filtros de segmentacao de empresa (Tipo + Setor).
  const [selectedPapelOptions, setSelectedPapelOptions] = useState([]);
  const [selectedSetorOptions, setSelectedSetorOptions] = useState([]);
  const [areaFilterMode, setAreaFilterMode] = useState('com');

  // Options do filtro de estágio: derivadas de ofertas.status REAIS (fonte da verdade
  // para estágio é oferta, não empresa — Fix filtros_dashboard 2026-04-26).
  // Label vem de dealStages quando conhecido; fallback para o próprio valor.
  const dealStageSelectOptions = useMemo(() => {
    const labelMap = new Map((dealStages || []).map(s => [s.value, s.label]));
    const stageSet = new Set(
      (ofertas || []).map(o => o.status).filter(Boolean)
    );
    return Array.from(stageSet)
      .sort()
      .map(v => ({ value: v, label: labelMap.get(v) || v }));
  }, [dealStages, ofertas]);
  const responsavelSelectOptions = colaboradores.filter(c => c.id !== 'all').map(c => ({ value: c.id, label: c.nome }));
  const areaSelectOptions = useMemo(
    () => areas.map(a => ({ value: a.id, label: a.nome })),
    [areas]
  );

  // ofertasPorEmpresa precisa ser declarado antes de filteredContatos (usado no predicado
  // do filtro de estagio — Fix filtros_dashboard 2026-04-26).
  const ofertasPorEmpresa = useMemo(() => {
    const map = new Map();
    (ofertas || []).forEach(oferta => {
      if (!oferta.empresa_id) return;
      if (!map.has(oferta.empresa_id)) map.set(oferta.empresa_id, []);
      map.get(oferta.empresa_id).push(oferta);
    });
    map.forEach(arr => {
      arr.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });
    });
    return map;
  }, [ofertas]);

  // Area em modo 'sem' (prospecção) eh exclusao empresa-level e nao entra no
  // predicado de oferta abaixo. Em modo 'com' a area participa do predicado AND-at-offer-level.
  const empresasParaExcluirPorAreaSem = useMemo(() => {
    if (selectedAreaOptions.length === 0 || areaFilterMode !== 'sem') return null;
    const areaIds = new Set(selectedAreaOptions.map(o => o.value));
    return new Set(
      (ofertas || [])
        .filter(o => o.area_id && areaIds.has(o.area_id))
        .map(o => o.empresa_id)
        .filter(Boolean)
    );
  }, [ofertas, selectedAreaOptions, areaFilterMode]);

  // Predicado per-oferta: AND-at-offer-level. Uma oferta passa se satisfaz
  // simultaneamente TODOS os filtros de nivel-oferta ativos (area em 'com',
  // estagio, responsavel). Empresa eh visivel se >= 1 oferta dela passa.
  // Fix combined_filters_offer_and 2026-04-30: antes os filtros eram aplicados
  // separadamente no nivel empresa (OR de empresas com qualquer oferta em cada
  // filtro), gerando falsos positivos quando ofertas DIFERENTES casavam cada
  // filtro mas nenhuma casava todos juntos.
  const hasOfferLevelFilter =
    selectedStageOptions.length > 0 ||
    selectedResponsavelOptions.length > 0 ||
    (selectedAreaOptions.length > 0 && areaFilterMode === 'com');

  const ofertaPassaFiltrosOferta = useMemo(() => {
    const areaIds = new Set(selectedAreaOptions.map(o => o.value));
    const stageSet = new Set(selectedStageOptions.map(o => o.value));
    const responsavelSet = new Set(selectedResponsavelOptions.map(o => o.value));
    const applyAreaInclude = areaIds.size > 0 && areaFilterMode === 'com';
    const hasStageFilter = stageSet.size > 0;
    const hasResponsavelFilter = responsavelSet.size > 0;

    return (oferta) => {
      if (!oferta) return false;
      if (applyAreaInclude && !(oferta.area_id && areaIds.has(oferta.area_id))) return false;
      if (hasStageFilter && !(oferta.status && stageSet.has(oferta.status))) return false;
      if (hasResponsavelFilter && !(oferta.responsavel_id && responsavelSet.has(oferta.responsavel_id))) return false;
      return true;
    };
  }, [selectedAreaOptions, selectedStageOptions, selectedResponsavelOptions, areaFilterMode]);

  const ofertasVisiveisPorEmpresa = useMemo(() => {
    if (!hasOfferLevelFilter) return ofertasPorEmpresa;
    const out = new Map();
    ofertasPorEmpresa.forEach((arr, empId) => {
      const visiveis = arr.filter(o => ofertaPassaFiltrosOferta(o));
      if (visiveis.length > 0) out.set(empId, visiveis);
    });
    return out;
  }, [ofertasPorEmpresa, ofertaPassaFiltrosOferta, hasOfferLevelFilter]);

  // Sessão 5.1: filtros de papel e status (ADR-001 F2)
  // IMPORTANTE: estes states DEVEM estar antes do useMemo que os referencia (TDZ)
  const [mostrarOperacionais, setMostrarOperacionais] = useState(false);
  const [mostrarInativas, setMostrarInativas] = useState(false);

  const filteredContatos = useMemo(() => {
    let filtered = contatos || [];
    const selectedPapelValues = selectedPapelOptions.map(o => o.value);
    const papelFilterExplicit = selectedPapelValues.length > 0;
    const papelSetExplicit = new Set(selectedPapelValues);

    // Sessão 5.1 + Fix filtros_dashboard 2026-04-26:
    // - Se usuario selecionou Tipos explicitamente: usa essa lista.
    // - Senao: default historico (buyer sempre; service_provider apenas com toggle).
    // - Toggle "Inativas" continua governando status_operacional.
    filtered = filtered.filter(c => {
      const emp = c.empresas;
      if (!emp) return true;
      const papel = emp.papel_negocio || 'buyer';
      const status = emp.status_operacional || 'active';

      const papelOk = papelFilterExplicit
        ? papelSetExplicit.has(papel)
        : (papel === 'buyer' || (mostrarOperacionais && papel === 'service_provider'));
      const statusOk = mostrarInativas || status === 'active';
      return papelOk && statusOk;
    });

    // Filtro por setores (array na coluna empresas.setores). Match se intersecao nao-vazia.
    const selectedSetorValues = selectedSetorOptions.map(o => o.value);
    if (selectedSetorValues.length > 0) {
      const setorSet = new Set(selectedSetorValues);
      filtered = filtered.filter(c => {
        const empSetores = c.empresas?.setores || [];
        return Array.isArray(empSetores) && empSetores.some(s => setorSet.has(s));
      });
    }
    if (searchTerm) {
       const lowerSearchTerm = searchTerm.toLowerCase();
       filtered = filtered.filter(c =>
         (c.empresas?.empresa?.toLowerCase().includes(lowerSearchTerm)) ||
         (c.first_name?.toLowerCase().includes(lowerSearchTerm)) ||
         (c.last_name?.toLowerCase().includes(lowerSearchTerm)) ||
         (c.email?.toLowerCase().includes(lowerSearchTerm))
       );
    }
    // Fix combined_filters_offer_and 2026-04-30: filtros de oferta (area em 'com',
    // estagio, responsavel) sao aplicados em conjunto via predicado per-oferta.
    // Empresa passa se >= 1 oferta dela satisfaz TODOS os filtros simultaneamente.
    if (hasOfferLevelFilter) {
      filtered = filtered.filter(c => {
        const empId = c.empresas?.id;
        if (!empId) return false;
        const ofertasDaEmpresa = ofertasPorEmpresa.get(empId) || [];
        return ofertasDaEmpresa.some(o => ofertaPassaFiltrosOferta(o));
      });
    }

    // Area em modo 'sem' (prospecção) continua sendo exclusao empresa-level.
    if (empresasParaExcluirPorAreaSem) {
      filtered = filtered.filter(c => c.empresas?.id && !empresasParaExcluirPorAreaSem.has(c.empresas.id));
    }
    return filtered;
  }, [contatos, searchTerm, hasOfferLevelFilter, ofertaPassaFiltrosOferta, empresasParaExcluirPorAreaSem, mostrarOperacionais, mostrarInativas, ofertasPorEmpresa, selectedPapelOptions, selectedSetorOptions]);

  const stats = useMemo(() => {
    if (!filteredContatos) return { empresas: 0, contatos: 0 };
    const realContatos = filteredContatos.filter(c => !c._isVirtual);
    return {
      empresas: new Set(filteredContatos.map(c => c.empresas?.empresa)).size,
      contatos: realContatos.length,
    };
  }, [filteredContatos]);

  const empresasUnicas = useMemo(() => {
    if (!contatos) return [];
    const map = new Map();
    contatos.forEach(c => {
      if (c.empresas && c.empresas.id && !map.has(c.empresas.id)) {
        map.set(c.empresas.id, c.empresas);
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.empresa || '').localeCompare(b.empresa || '')
    );
  }, [contatos]);

  // Modais existentes
  const [showFUPModal, setShowFUPModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateEmpresaModal, setShowCreateEmpresaModal] = useState(false);
  const [showImportVCardModal, setShowImportVCardModal] = useState(false);
  const [showCreateOfertaModal, setShowCreateOfertaModal] = useState(false);
  const [showCreateAreaModal, setShowCreateAreaModal] = useState(false);
  const [showAreasListModal, setShowAreasListModal] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedOfertaId, setSelectedOfertaId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyForNewContact, setCompanyForNewContact] = useState(null);
  const [contactsForFUP, setContactsForFUP] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAIContextGlobal, setShowAIContextGlobal] = useState(false);
  const [aiContextEmpresa, setAIContextEmpresa] = useState(null);

  // NOVO (Sessão 4): Inbox de emails pendentes
  const [showEmailsPendentes, setShowEmailsPendentes] = useState(false);
  // NOVO (Sessão 4.8): Hub de busca de emails
  const [showEmailsHub, setShowEmailsHub] = useState(false);
  // Sessão 4.9.2: modal de oportunidades escondidas acionado via botão
  const [showOportunidades, setShowOportunidades] = useState(false);
  // Sessão 5.0: modal de curadoria de domínios novos
  const [showDominiosNovos, setShowDominiosNovos] = useState(false);
  // HANDOFF 21/abr/2026 AÇÃO 3: modal de enriquecimento Apollo
  const [showApolloEnrich, setShowApolloEnrich] = useState(false);
  // HANDOFF 21/abr/2026 AÇÃO 4 F8: painel de revisão de evidências IA
  const [showF8, setShowF8] = useState(false);
  const [f8InitialFilter, setF8InitialFilter] = useState(null);

  // Ouve CustomEvent emitido pelo PendingCandidatesCard (AgentActionsPanel) para abrir F8 com contexto.
  // TODO: F8EvidenceReviewModal não suporta initialFilter ainda. Abre F8 global; filtro granular
  // por empresa_id/focus será implementado na próxima passagem pelo componente F8.
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setShowF8(true);
      setF8InitialFilter(detail.empresaId ? detail : null);
      if (detail.empresaId) {
        console.info('[open-f8-modal] context recebido (F8 ainda abre global; filtro pendente):', detail);
      }
    };
    window.addEventListener('open-f8-modal', handler);
    return () => window.removeEventListener('open-f8-modal', handler);
  }, []);
  const { pendingCount: f8PendingCount } = useEvidences();
  // NOVO (Sessão 4.9): empresa pré-preenchida ao criar oferta via widget
  const [ofertaPreEmpresaId, setOfertaPreEmpresaId] = useState(null);

  const handleCriarOfertaViaWidget = (empresaId) => {
    setOfertaPreEmpresaId(empresaId);
    setShowCreateOfertaModal(true);
  };
  // Sessão 4.9.1: drawer global compartilhado pelos EmailsHubModal
  const [emailDrawerSelecionado, setEmailDrawerSelecionado] = useState(null);
  const handleAbrirEmailDrawer = (email) => setEmailDrawerSelecionado(email);
  // Fix 3 dashboard_ux_2 2026-04-26: enrich Apollo do contato
  const [enrichContact, setEnrichContact] = useState(null); // { contato, empresa }
  const handleEnrichContact = (contato, empresa) => setEnrichContact({ contato, empresa });

  const handleLogout = async () => { await signOut(); };
  const handleOpenFUP = (empresa, companyContacts) => {
    setSelectedCompany(empresa);
    setContactsForFUP(companyContacts);
    setShowFUPModal(true);
  };
  const handleOpenEmail = (contact) => { setSelectedContact(contact); setShowEmailModal(true); };
  const handleOpenCreateContact = (empresa) => { setCompanyForNewContact(empresa); setShowCreateModal(true); };
  const handleOpenHistory = (empresa) => { setSelectedCompany(empresa); setShowHistoryModal(true); };
  const handleOpenAIContextEmpresa = (empresa) => {
    setAIContextEmpresa({ id: empresa.id, nome: empresa.empresa || empresa.company_name });
  };
  const handleCreateEmpresa = async (empresaData) => {
    const result = await createEmpresa(empresaData);
    if (result.success) setShowCreateEmpresaModal(false);
  };
  const handleCreateOferta = async (payload) => {
    await createOferta(payload);
    await refetchOfertas();
  };
  const handleOfertaClick = (ofertaId) => setSelectedOfertaId(ofertaId);
  const handleOpenArea = (area) => {
    setSelectedArea(area);
    setShowAreasListModal(false);
  };
  const handleCloseArea = () => setSelectedArea(null);
  const handleNewAreaFromList = () => {
    setShowAreasListModal(false);
    setShowCreateAreaModal(true);
  };

  const showAreaModeToggle = selectedAreaOptions.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <DashboardHeader
        user={user}
        emailStats={emailStats}
        f8PendingCount={f8PendingCount}
        onLogout={handleLogout}
        onOpenCreateEmpresa={() => setShowCreateEmpresaModal(true)}
        onOpenCreateOferta={() => setShowCreateOfertaModal(true)}
        onOpenCreateArea={() => setShowCreateAreaModal(true)}
        onOpenAreasList={() => setShowAreasListModal(true)}
        onOpenInbox={() => setShowEmailsPendentes(true)}
        onOpenEmailsHub={() => setShowEmailsHub(true)}
        onOpenOportunidades={() => setShowOportunidades(true)}
        onOpenDominios={() => setShowDominiosNovos(true)}
        onOpenApollo={() => setShowApolloEnrich(true)}
        onOpenF8={() => setShowF8(true)}
        onOpenAIContext={() => setShowAIContextGlobal(true)}
      />

      <div className="w-full px-4 lg:px-6 2xl:px-8 py-6">
        {/* Fix 1 dashboard_ux 2026-04-26: filtros sticky para permanecerem visiveis no scroll.
            top-0 porque o header principal NAO e sticky (ele rola junto). */}
        <div className="bg-white p-4 rounded-lg shadow-sm border mb-6 sticky top-0 z-30">
          {/* dashboard_ux 2026-04-29: 6 filtros em 1 linha; popover com hover-to-open
              (delays anti-flicker) + busca + Concluir + ESC + X clear. */}
          <div className="grid grid-cols-12 gap-3 items-start relative z-20">
            <div className="relative col-span-2">
              <SearchIcon className="absolute left-3 top-[11px] text-slate-400 z-10" size={20} />
              <input
                type="text"
                placeholder="Buscar empresa/contato..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg h-[42px] text-sm"
              />
            </div>
            <div className="col-span-2">
              <FilterPopover
                icon={Tag}
                placeholder="Tipo..."
                searchPlaceholder="Buscar tipo..."
                options={papelOptions}
                value={selectedPapelOptions}
                onChange={setSelectedPapelOptions}
                compactNoun="tipos"
              />
            </div>
            <div className="col-span-2">
              <FilterPopover
                icon={Layers}
                placeholder="Setor..."
                searchPlaceholder="Buscar setor..."
                options={setorOptions}
                value={selectedSetorOptions}
                onChange={setSelectedSetorOptions}
                compactNoun="setores"
              />
            </div>
            <div className="col-span-2">
              <FilterPopover
                icon={FilterIcon}
                placeholder="Estágio..."
                searchPlaceholder="Buscar estágio..."
                options={dealStageSelectOptions}
                value={selectedStageOptions}
                onChange={setSelectedStageOptions}
                compactNoun="estágios"
              />
            </div>
            <div className="col-span-2">
              <FilterPopover
                icon={MapPin}
                placeholder={loadingAreas ? 'Carregando...' : 'Área...'}
                searchPlaceholder="Buscar área..."
                options={areaSelectOptions}
                value={selectedAreaOptions}
                onChange={setSelectedAreaOptions}
                compactNoun="áreas"
                disabled={loadingAreas}
              />
            </div>
            <div className="col-span-2">
              <FilterPopover
                icon={User}
                placeholder="Responsável..."
                searchPlaceholder="Buscar responsável..."
                options={responsavelSelectOptions}
                value={selectedResponsavelOptions}
                onChange={setSelectedResponsavelOptions}
                compactNoun="resp."
              />
            </div>
          </div>

          {/* Sessão 5.1 + filtros_compactos 2026-04-26: toggles + contadores inline */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500 mr-1">Visibilidade:</span>
              <button
                onClick={() => setMostrarOperacionais(v => !v)}
                className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                  mostrarOperacionais
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                title="Incluir advocacias, consultorias, cartórios (papel=operacional)"
              >
                ⚖️ Operacionais
              </button>
              <button
                onClick={() => setMostrarInativas(v => !v)}
                className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                  mostrarInativas
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                title="Incluir empresas liquidadas, arquivadas, inativas"
              >
                ⚠️ Inativas
              </button>
              {showAreaModeToggle && areaFilterMode === 'sem' && (
                <span className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-amber-800 text-[11px]">
                  <XCircle size={11} />
                  Modo prospecção: empresas ainda sem oferta
                </span>
              )}
              {showAreaModeToggle && (
                <>
                  <span className="text-slate-300 mx-1">|</span>
                  <span className="text-xs text-slate-600 font-medium">
                    Empresas com oferta na{selectedAreaOptions.length > 1 ? 's' : ''} área{selectedAreaOptions.length > 1 ? 's' : ''} selecionada{selectedAreaOptions.length > 1 ? 's' : ''}:
                  </span>
                  <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
                    <button
                      onClick={() => setAreaFilterMode('com')}
                      className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
                        areaFilterMode === 'com'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <CheckCircle2 size={12} />
                      Com oferta
                    </button>
                    <button
                      onClick={() => setAreaFilterMode('sem')}
                      className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors border-l border-slate-300 ${
                        areaFilterMode === 'sem'
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      title="Útil para prospectar empresas ainda não contatadas"
                    >
                      <XCircle size={12} />
                      Sem oferta
                    </button>
                  </div>
                  {areaFilterMode === 'sem' && (
                    <span className="text-xs text-amber-700 italic">
                      → mostrando empresas candidatas para prospecção
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-slate-700">{stats.empresas}</span>
              <span className="text-slate-500">Empresas</span>
              <span className="text-slate-300">|</span>
              <span className="font-semibold text-slate-700">{stats.contatos}</span>
              <span className="text-slate-500">Contatos</span>
              <span className="text-slate-300">|</span>
              <button
                type="button"
                onClick={() => setShowImportVCardModal(true)}
                title="Importar contatos a partir de arquivo .vcf"
                className="px-2 py-1 text-[11px] bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 flex items-center gap-1"
              >
                📥 Importar vCard
              </button>
            </div>
          </div>
        </div>


        {(loading || loadingOptions) ? (
          <div className="flex items-center justify-center h-64"><div className="text-slate-600">Carregando dados...</div></div>
        ) : (
          <ContactTable
            contatos={filteredContatos}
            ofertasPorEmpresa={ofertasVisiveisPorEmpresa}
            onOfertaClick={handleOfertaClick}
            onEditFUP={handleOpenFUP}
            onSendEmail={handleOpenEmail}
            onUpdateContact={updateContact}
            onUpdateEmpresa={updateEmpresa}
            onAddContact={handleOpenCreateContact}
            onOpenHistory={handleOpenHistory}
            onOpenAIContext={handleOpenAIContextEmpresa}
            updateOferta={updateOferta}
            buildHITLPatchManual={buildHITLPatchManual}
            buildHITLPatchDataFup={buildHITLPatchDataFup}
            highlightedStages={selectedStageOptions.map(o => o.value)}
            briefingByOferta={briefingByOferta}
            ppCountByOferta={ppCountByOferta}
            onEnrichContact={handleEnrichContact}
            employmentStatusMap={employmentStatusMap}
            focusEmpresaId={focusEmpresaId}
            selectedAreaIds={selectedAreaOptions.map(o => o.value)}
            areaFilterMode={areaFilterMode}
          />
        )}
      </div>

      {/* Modais */}
      {showFUPModal && selectedCompany && (
        <FUPModal
          empresa={selectedCompany} companyContacts={contactsForFUP}
          onClose={() => setShowFUPModal(false)}
          onSave={async (updates) => {
            await updateEmpresa(selectedCompany.id, updates);
            setShowFUPModal(false);
          }}
        />
      )}

      {showEmailModal && selectedContact && (
        <EmailModal contact={selectedContact} onClose={() => setShowEmailModal(false)} />
      )}

      {showCreateModal && companyForNewContact && (
        <ContactQuickAddModal
          empresa={companyForNewContact}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); }}
        />
      )}

      {showImportVCardModal && (
        <VCardImportModal
          onClose={() => setShowImportVCardModal(false)}
          onAfterImport={() => { /* useContacts realtime sub atualiza lista */ }}
        />
      )}

      {showHistoryModal && selectedCompany && (
        <HistoryModal empresa={selectedCompany} onClose={() => setShowHistoryModal(false)} />
      )}

      {showCreateEmpresaModal && (
        <CreateEmpresaModal
          onClose={() => setShowCreateEmpresaModal(false)}
          onSave={handleCreateEmpresa}
        />
      )}

      {showCreateOfertaModal && (
        <CreateOfertaModal
          empresas={empresasUnicas}
          preselectedEmpresaId={ofertaPreEmpresaId}
          onClose={() => { setShowCreateOfertaModal(false); setOfertaPreEmpresaId(null); }}
          onSave={handleCreateOferta}
        />
      )}

      {showCreateAreaModal && (
        <CreateAreaModal
          onClose={() => setShowCreateAreaModal(false)}
          onSave={createArea}
        />
      )}

      {showAreasListModal && (
        <AreasListModal
          areas={areas}
          onClose={() => setShowAreasListModal(false)}
          onOpenArea={handleOpenArea}
          onNewArea={handleNewAreaFromList}
        />
      )}

      {selectedArea && (
        <AreaDetailModal
          area={selectedArea}
          onClose={handleCloseArea}
          onUpdateArea={async (id, updates) => {
            await updateArea(id, updates);
            setSelectedArea(prev => prev ? { ...prev, ...updates } : prev);
          }}
          onDetectSuccess={refetchOfertas}
        />
      )}

      {selectedOfertaId && (
        <OfertaDetailModal
          ofertaId={selectedOfertaId}
          onClose={() => {
            setSelectedOfertaId(null);
            // ADR-015 Fase 2.1: refetch dados que podem ter mudado no modal
            refetchPPCounts();
            refetchOfertas();
          }}
        />
      )}

      {showAIContextGlobal && (
        <AIContextGlobalModal onClose={() => setShowAIContextGlobal(false)} />
      )}

      {aiContextEmpresa && (
        <AIContextPanelEmpresa
          empresaId={aiContextEmpresa.id}
          empresaNome={aiContextEmpresa.nome}
          onClose={() => setAIContextEmpresa(null)}
        />
      )}

      {showEmailsPendentes && (
        <EmailsPendentesModal onClose={() => setShowEmailsPendentes(false)} />
      )}

      {showEmailsHub && (
        <EmailsHubModal
          modo="busca"
          onClose={() => setShowEmailsHub(false)}
          onOpenEmail={handleAbrirEmailDrawer}
          onOpenOferta={(o) => setSelectedOfertaId(o.id)}
        />
      )}

      {showOportunidades && (
        <OportunidadesEscondidasModal
          onClose={() => setShowOportunidades(false)}
          onCriarOferta={handleCriarOfertaViaWidget}
          onOpenEmail={handleAbrirEmailDrawer}
        />
      )}

      {showDominiosNovos && (
        <DominiosNovosModal
          onClose={() => setShowDominiosNovos(false)}
          onDominioProcessado={() => {}}
        />
      )}

      {showApolloEnrich && (
        <ApolloEnrichModal
          onClose={() => setShowApolloEnrich(false)}
          userEmail={user?.email}
        />
      )}

      {showF8 && (
        <F8EvidenceReviewModal
          onClose={() => { setShowF8(false); setF8InitialFilter(null); }}
          userEmail={user?.email}
          initialFilter={f8InitialFilter}
        />
      )}

      {emailDrawerSelecionado && (
        <EmailDetailDrawer
          email={emailDrawerSelecionado}
          empresas={empresasUnicas}
          areas={areas}
          ofertas={ofertas}
          onClose={() => setEmailDrawerSelecionado(null)}
        />
      )}

      {enrichContact && (
        <ApolloEnrichContactModal
          contato={enrichContact.contato}
          empresa={enrichContact.empresa}
          userEmail={user?.email}
          onClose={() => setEnrichContact(null)}
          onApplied={() => {
            // useContacts tem realtime subscription em 'contatos' — refresh automatico.
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
